// Turning readings into something a chart can draw.
//
// A channel's type decides how its history can be shown. Numbers and integers
// are measurements and keep their value. Booleans are states: they are plotted
// at 0 and 1 so the same chart component can draw them as a step line, and they
// are turned back into words only at the edges (the axis and the tooltip).
// Text has no meaningful shape over time and is not chartable at all.
//
// Reading values arrive losslessly: an integer beyond 2^53 comes as a tagged
// big integer, which is numeric rather than boolean, so it must be converted
// before the type check.

import { isJsonBigInteger } from '@/lib/json';

/** The shape a channel's history is drawn with. */
export type ChartKind = 'numeric' | 'boolean';

/** A point as the chart component receives it. Boolean states are 0 and 1. */
export interface HistoryPoint {
  time: number;
  value: number;
}

/** The newest reading that could be plotted, as the reader should see it. */
export interface HistorySeries {
  points: HistoryPoint[];
  latest: number | boolean | null;
  latestAt: string | null;
}

/** The smallest input shape both the dashboard and the readings screen have. */
interface ReadingLike {
  value: unknown;
  observedAt?: string | null;
}

/** Whether a channel can be charted, and with which shape. */
export function channelChartKind(channel: { type: string }): ChartKind | null {
  if (channel.type === 'number' || channel.type === 'integer') return 'numeric';
  if (channel.type === 'boolean') return 'boolean';
  return null;
}

/**
 * Build an ascending series from a page of readings.
 *
 * The chart draws oldest first. The newest value comes from the greatest
 * observation time rather than from the input order, so a history listed either
 * way reports the same "latest" reading. Items whose observation time is
 * unknown cannot be placed on a time axis and are left out rather than guessed
 * at. Ordering is local to the series: the caller's pagination is never
 * reordered.
 */
export function buildHistory(items: ReadonlyArray<ReadingLike>, kind: ChartKind): HistorySeries {
  const points: HistoryPoint[] = [];
  let latestAt: string | null = null;

  items.forEach((item) => {
    const observedAt = item.observedAt;
    if (!observedAt) return;
    const time = new Date(observedAt).getTime();
    if (!Number.isFinite(time)) return;

    const value = kind === 'boolean' ? booleanPointValue(item.value) : numericValue(item.value);
    if (value === null) return;

    points.push({ time, value });
    if (latestAt === null || time > new Date(latestAt).getTime()) {
      latestAt = observedAt;
    }
  });

  points.sort((a, b) => a.time - b.time);

  const newest = points[points.length - 1];
  return {
    points,
    // A boolean is reported as the state the reader saw, not as its 0/1 plot
    // position.
    latest: newest === undefined ? null : kind === 'boolean' ? newest.value === 1 : newest.value,
    latestAt,
  };
}

/** A numeric point, or null when the value is not a finite number. */
function numericValue(value: unknown): number | null {
  const numeric = isJsonBigInteger(value) ? Number(value.bigint) : value;
  return typeof numeric === 'number' && Number.isFinite(numeric) ? numeric : null;
}

/** A boolean point as 0 or 1, or null when the value is not a boolean. */
function booleanPointValue(value: unknown): number | null {
  if (typeof value !== 'boolean') return null;
  return value ? 1 : 0;
}
